import React from "react";
import { Link } from "gatsby";
import { useStaticQuery, graphql } from "gatsby";
import cx from "classnames";
import JobPost from "~/models/job-post";
import Location from "~/views/identity/location";
import JPY from "~/views/identity/jpy";

type Props = {
  className?: string;
};

function SideNavigationJobPost({ className }: Props) {
  const { jobPosts }: any = useStaticQuery(
    graphql`
      query SideNavigationJobPost {
        jobPosts: allJobPostMdx(
          sort: { frontmatter: { createdAt: DESC } }
          limit: 4
          filter: {
            frontmatter: { closed: { ne: true }, isAutoGenerated: { ne: true } }
          }
        ) {
          nodes {
            id
            frontmatter {
              id
              title
              company {
                yamlId
                name
                logo {
                  imgix {
                    url(imgixParams: { w: 80, fm: "webp", dpr: 2 })
                  }
                }
              }
              locations {
                yamlId
                name
                slug
              }
              minimumSalary
              maximumSalary
              createdAt
              updatedAt
              closed
              isAutoGenerated
            }
            body
            excerpt
          }
          pageInfo {
            currentPage
            hasNextPage
            hasPreviousPage
            pageCount
          }
        }
      }
    `
  );

  const jobPostList = jobPosts.nodes.map(
    (jobPost: any) => new JobPost(jobPost)
  );

  return (
    <div className={cx("mb-16", className)}>
      <p className="font-extrabold text-4xl text-neutral-90 mb-6 leading-[1.2]">
        Featured Jobs
      </p>

      <div>
        {jobPostList.map((jobPost: JobPost) => (
          <Link
            key={jobPost.id}
            to={`/jobs/${jobPost.id}/`}
            className={cx(
              className,
              "bg-neutral-99 flex items-start rounded-lg p-3 gap-3 mb-6"
            )}>
            <div className="flex-1 relative bg-white rounded flex justify-center items-center">
              {jobPost.company.logo.imgix?.url && (
                <img
                  src={jobPost.company.logo.imgix.url}
                  alt={jobPost.title}
                  loading="eager"
                  style={{ objectFit: "contain" }}
                  className="rounded-lg overflow-hidden isolate transition-all ease-in-out duration-500 hover:opacity-[0.85] hover:scale-[1.005]"
                />
              )}
            </div>

            <div className="flex-[3] sm:py-1 shrink-0">
              <div>
                <div className="flex justify-between items-center mb-1 md:mb-2">
                  <div className="font-medium px-2 block text-3xs leading-[1.4em]">
                    {jobPost.company.name}
                  </div>
                </div>

                <div className="hover:highlighted-link px-2 block font-bold md:text-base leading-[1.4em]">
                  {jobPost.title}
                </div>
              </div>

              <div className="px-2 flex-col flex gap-2 mt-2 md:mt-3">
                <div className="flex gap-1 items-center">
                  <Location />
                  <span className="text-3xs text-neutral-60 font-medium">
                    {jobPost.locations
                      .map((location) => location.name)
                      .join("/")}
                  </span>
                </div>

                <div className="flex gap-1 items-center">
                  <JPY />
                  <span className="text-3xs text-neutral-60 font-medium">
                    {!!jobPost.minimumSalary && (
                      <span>{`${jobPost.minimumSalary}万円`}</span>
                    )}

                    {(!!jobPost.minimumSalary || !!jobPost.maximumSalary) && (
                      <span>〜</span>
                    )}

                    {!!jobPost.maximumSalary && (
                      <span>{`${jobPost.maximumSalary}万円`}</span>
                    )}

                    {!jobPost.minimumSalary && !jobPost.maximumSalary && (
                      <span>非公開</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default SideNavigationJobPost;
